import { useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Box } from "lucide-react";
import { DEMOS } from "@/src/data/demos";
import { getOutcomes } from "@/src/data/demo-outcomes";
import { useSession } from "@/src/lib/session";
import DemoLivePreview from "@/src/components/DemoLivePreview";
import OutcomeTimeline from "@/src/components/OutcomeTimeline";
import { motion } from "motion/react";

export default function DemoDetail() {
  const { demoId } = useParams<{ demoId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addVisit } = useSession();

  const demo = DEMOS.find((d) => d.id === demoId);
  const regionParam = searchParams.get("region");
  const outcomes = demo ? getOutcomes(demo.id) : null;
  const displayName = demo?.name.replace(/\s*Agentic Operating System \(Application\)/i, " Agentic Application");

  // Iteration 4: Track this demo visit in session memory.
  // Runs once per demo ID change. Safe against missing demos.
  useEffect(() => {
    if (demo?.id) {
      addVisit(demo.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo?.id]);

  if (!demo) {
    return (
      <div className="p-8 flex flex-col items-center justify-center h-full">
        <h2 className="text-2xl font-bold">Demo not found</h2>
        <Link to="/" className="text-emerald-600 hover:underline mt-4">Return to Explore</Link>
      </div>
    );
  }

  const handleBuildMine = () => {
    navigate("/build", { state: { demo } });
  };

  const handleExploreLiveDemo = () => {
    if (!demo) return;
    const regionSuffix = regionParam ? `?region=${regionParam}` : "";
    navigate(`/explore/${demo.id}${regionSuffix}`);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="h-full overflow-y-auto flex flex-col perspective-1000 bg-slate-50"
    >
      {/* Back Link */}
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-7xl mx-auto px-8 py-6 relative z-10"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Explore
        </Link>
      </motion.div>

      {/* Hero */}
      <div className="w-full max-w-7xl mx-auto px-8 pb-16 pt-4 border-b border-slate-100 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="max-w-4xl flex flex-col gap-6"
        >
          <div>
            <motion.span 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-block px-3 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-semibold rounded-full uppercase tracking-wider mb-4 shadow-sm"
            >
              {demo.category}
            </motion.span>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-5xl font-semibold tracking-tight text-slate-900 mb-4"
            >
              {displayName}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-slate-700 font-medium leading-snug"
            >
              {demo.tagline}
            </motion.p>
          </div>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-base text-slate-600 leading-relaxed"
          >
            {demo.description}
          </motion.p>
        </motion.div>
      </div>

      {/* Outcome Timeline — what becomes possible at week 1 / month 1 / quarter 1 */}
      {outcomes && <OutcomeTimeline outcomes={outcomes} />}

      <motion.section
        initial={{ opacity: 0, y: 36 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-7xl mx-auto px-8 pt-16 pb-6 relative z-10"
      >
        <div className="grid gap-8 lg:grid-cols-[0.86fr_1.14fr] lg:items-center">
          <div>
            <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-700">
              Live Agentic Application shell
            </span>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-slate-900">
              Open the working application shape for this exact vertical.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              This is a small window into the live shell. Click Explore live demo to enter the full Agentic Application: Mission Control, vertical workspace screens, Agent Library, Integrations, Decisions, Receipts, Weekly Review, and permission paths adapted around {demo.vocabulary.tracks}.
            </p>
            <button
              onClick={handleExploreLiveDemo}
              className="mt-7 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
            >
              Explore live demo
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>

          <DemoLivePreview
            demoId={demo.id}
            productName={demo.name}
            gradient={demo.gradient}
            defaultRegion={regionParam || "usa"}
            onExploreLive={handleExploreLiveDemo}
          />
        </div>
      </motion.section>

      {/* What's Inside & Vocab */}
      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto px-8 py-20 flex flex-col gap-16 relative z-10"
      >
        <div>
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 mb-3">Screens and agents inside</h2>
          <p className="text-slate-500 mb-8 max-w-2xl">
            These are the workspace screens, agent work areas, and operational surfaces the Architect would start from before adapting the application to your exact noun, stages, data sources, and permissions.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {demo.modules.map((mod, i) => (
              <motion.div 
                key={mod} 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-start gap-4 hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200 text-slate-700 shadow-sm">
                  <Box className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">{mod}</h4>
                  <p className="text-sm text-slate-500 mt-1">Agentic Application workspace surface</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-8">
            <h2 className="text-3xl font-semibold tracking-tight text-slate-900 mb-2">The language this Agentic Application understands</h2>
            <p className="text-slate-500">When you build yours, the Architect uses your actual noun, stages, counterparties, and repeated work — not generic software labels.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div whileHover={{ y: -5 }} className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col h-full">
              <h4 className="text-sm font-semibold tracking-wider text-slate-400 uppercase mb-4">Tracks</h4>
              <p className="text-2xl font-medium text-slate-900 mt-auto">{demo.vocabulary.tracks}</p>
            </motion.div>
            <motion.div whileHover={{ y: -5 }} className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col h-full">
              <h4 className="text-sm font-semibold tracking-wider text-slate-400 uppercase mb-4">Stages</h4>
              <p className="text-lg font-medium text-slate-900 mt-auto">{demo.vocabulary.stages}</p>
            </motion.div>
            <motion.div whileHover={{ y: -5 }} className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col h-full">
              <h4 className="text-sm font-semibold tracking-wider text-slate-400 uppercase mb-4">Works With</h4>
              <p className="text-2xl font-medium text-slate-900 mt-auto">{demo.vocabulary.worksWith}</p>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Final CTA */}
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="bg-slate-900 text-white w-full py-20 px-8 flex justify-center mt-12 overflow-hidden relative shrink-0"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-500/20 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-xl text-center flex flex-col items-center gap-8 relative z-10">
          <h2 className="text-4xl font-semibold tracking-tight">
            Ready to build yours?
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleBuildMine}
              className="bg-white text-slate-900 px-10 py-4 rounded-xl text-lg font-medium hover:bg-slate-100 transition-colors"
            >
              Build my Agentic Application
            </button>
            <button
              onClick={handleExploreLiveDemo}
              className="bg-transparent border border-white/30 text-white px-10 py-4 rounded-xl text-lg font-medium hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              Explore live demo
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
